import { decodeJwtToken } from '@luna/utils';
import { addSeconds } from '@luna/date';
import { getEnv } from '@luna/env';

import { getAuthData, setAuthData } from '../authDataService';
import { removeBacklinkPathFromStorage } from '../backlinkPathService';
import {
    getCodeFromUrlParams,
    getCsrfTokenFromUrlStateParams,
    getIdTokenFromUrlParams,
    getSessionStateFromUrlParams,
} from './searchParamsUtils';
import type { AuthConfig, LoginStrategyOptions, LoginStrategyShape } from '../types';
import { fetchTokenurl } from '../tokenFetchUtils';
import type { GetTokenResponse } from './tokenResponseTypes';
import type { IdToken } from '../tokenTypes';
import { loadCsrfToken } from './CsrfTokenService';

function logout(environment: AuthConfig): void {
    sessionStorage.clear();
    window.location.replace(environment.logoutUrl.default);
}

export interface LoadTokenWithCodeWorkflowOptions {
    idToken?: string;
    sessionState?: string;
    code: string;
}

export const loadTokenWithCodeWorkflow = async (
    config: AuthConfig,
    loadTokenWithCodeWorkflowOptions?: LoadTokenWithCodeWorkflowOptions,
): Promise<void> => {
    if (getEnv().oidcCsrf) {
        const csrfTokenFromState = getCsrfTokenFromUrlStateParams();
        const csrfTokenFromStorage = loadCsrfToken();

        if (!csrfTokenFromState) {
            logout(config);
            return;
        }

        if (!csrfTokenFromStorage) {
            logout(config);
            return;
        }

        if (csrfTokenFromStorage !== csrfTokenFromState) {
            logout(config);
            return;
        }
    }

    const idToken = loadTokenWithCodeWorkflowOptions?.idToken || getIdTokenFromUrlParams();
    const sessionState =
        loadTokenWithCodeWorkflowOptions?.sessionState || getSessionStateFromUrlParams();
    let parsedIdToken: IdToken | undefined = idToken ? decodeJwtToken<IdToken>(idToken) : undefined;
    const authData = getAuthData();

    const data: GetTokenResponse = await fetchTokenurl(
        config,
        loadTokenWithCodeWorkflowOptions?.code,
    );
    if (!data.access_token) {
        logout(config);
        return;
    }

    const expiration = addSeconds(new Date(), data.expires_in).toISOString();

    if (!parsedIdToken && data.id_token) {
        parsedIdToken = decodeJwtToken<IdToken>(data.id_token);
    }

    setAuthData({
        token: data.access_token,
        idToken: idToken || data.id_token,
        tokenExpiration: expiration,
        refreshToken: data.refresh_token,
        nonce: authData?.nonce,
        clientId: parsedIdToken?.appid || parsedIdToken?.aud,
        sid: parsedIdToken?.sid,
        sessionState,
        codeVerifier: authData?.codeVerifier,
    });
    removeBacklinkPathFromStorage();
};

const isCodeAndIdTokenInUrl = (): boolean => {
    const codeFromUrlParams = getCodeFromUrlParams();
    const idTokenFromUrlParams = getIdTokenFromUrlParams();
    return !!codeFromUrlParams && !!idTokenFromUrlParams;
};

export const loadTokenWithPKCECodeStrategy = ({
    config,
}: LoginStrategyOptions): LoginStrategyShape => ({
    run: () => loadTokenWithCodeWorkflow(config),
    canRun: isCodeAndIdTokenInUrl,
});
